import {
  Box,
  Center,
  Clickable,
  getColorByPath,
  HStack,
  Icon,
  Integer,
  Show,
  ThemeColorPath,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {CSSProperties, KeyboardEvent, ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId} from 'shared/web';

import {useComponentsTheme} from '../../hooks/useComponentsTheme';
import {BaseFlagProps} from '../Flag/BaseFlagProps';
import {FlagLabel} from '../Flag/components/FlagLabel';
import {getColorsFromScheme} from '../Flag/getColorsFromScheme';
import {getFlagElementSizes} from '../Flag/getFlagElementSizes';

export type TagSizeType = 'default' | 'small';

export interface TagProps extends BaseFlagProps {
  onDelete?: () => void;
  onClick?: () => void;
}

const tagHoverVariants = cva(
  'h-full hover:bg-[var(--tag-hover-color)] hover:[box-shadow:var(--shadow-elevation-2)]'
);

type TagHoverBackgroundColorProps = {
  $hoverColorPath: ThemeColorPath;
  children?: ReactNode;
};

type TagHoverStyle = CSSProperties & {
  '--tag-hover-color': string;
};

export function TagHoverBackgroundColor(props: TagHoverBackgroundColorProps) {
  const theme = useComponentsTheme();
  const style: TagHoverStyle = {
    '--tag-hover-color':
      getColorByPath(props.$hoverColorPath, theme) ?? theme.colors.general.transparent,
  };

  return (
    <div className={cn(tagHoverVariants())} style={style}>
      {props.children}
    </div>
  );
}

export function Tag(props: TagProps) {
  const {backgroundColor, color, hoverColor} = getColorsFromScheme(
    props.colorScheme,
    props.isSubtle
  );
  const {height, textSize, labelHorizontalPadding, borderRadius} = getFlagElementSizes(props.size);

  const iconSize: Integer = props.size === 'small' ? 3 : 4;
  /**
   * We want the iconWrapper to be a rectangle
   */
  const iconWrapperWidth = height;

  return (
    <Clickable
      onClick={props.onClick}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          props.onClick?.();
        }
      }}
    >
      <Box
        borderRadius={borderRadius}
        height={height}
        backgroundColor={backgroundColor}
        overflow="hidden"
        tabIndex={props.onClick ? 0 : undefined}
        data-testid={suffixTestId('tagWrapper', props)}
      >
        <HStack justify="space-between" align="center" height="100%">
          <Box paddingHorizontal={labelHorizontalPadding} data-testid={suffixTestId('tag', props)}>
            <FlagLabel label={props.label} size={textSize} color={color} />
          </Box>

          <Show when={isNotNil(props.onDelete)}>
            <TagHoverBackgroundColor $hoverColorPath={hoverColor}>
              <Center height="100%" width={iconWrapperWidth}>
                <Icon
                  value="navigation/close"
                  size={iconSize}
                  color={color}
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onDelete?.();
                  }}
                  onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      props.onDelete?.();
                    }
                  }}
                  tabIndex={0}
                  data-testid={suffixTestId('tagIcon', props)}
                />
              </Center>
            </TagHoverBackgroundColor>
          </Show>
        </HStack>
      </Box>
    </Clickable>
  );
}
